import { Button } from '@/components/ui/button';
import { ArrowDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BackgroundGradientAnimation } from '@/components/ui/background-gradient-animation';
import { SiriOrb } from '@/components/ui/siri-orb';
import { useIsMobile } from '@/hooks/use-mobile';

const Hero = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header id="inicio" className="relative min-h-screen flex items-start md:items-center justify-center overflow-hidden" role="banner">
      <BackgroundGradientAnimation
        gradientBackgroundStart="rgb(0, 29, 75)" // AI4ME deep blue
        gradientBackgroundEnd="rgb(0, 8, 30)"
        firstColor="0, 29, 75"     // Deep blue
        secondColor="251, 146, 60" // Orange
        thirdColor="59, 130, 246"  // Blue
        fourthColor="6, 182, 212"  // Cyan
        fifthColor="251, 146, 60"  // Orange
        pointerColor="59, 130, 246"
        containerClassName="absolute inset-0"
      >
        {/* Tech Grid Background - Simplified for performance */}
        <div className="absolute inset-0 z-[1]">
          <div className="absolute inset-0 bg-[linear-gradient(rgba(59,130,246,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(59,130,246,0.05)_1px,transparent_1px)] bg-[size:50px_50px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,black,transparent)]" />
        </div>
        
        {/* Floating particles - reduced count on mobile for performance */}
        <div className="absolute inset-0 z-[2] overflow-hidden">
          {[...Array(isMobile ? 5 : 8)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-blue-400 rounded-full opacity-30"
              style={{
                left: `${(i * 12.5) % 100}%`,
                top: `${(i * 15) % 100}%`,
                animation: `float ${8 + i}s linear infinite`
              }}
            />
          ))}
        </div>
        
        {/* Corner Tech Decorations - Desktop only for performance */}
        {!isMobile && (
          <>
            <div className="absolute top-0 left-0 w-64 h-64 z-[4] opacity-20">
              <div className="absolute top-8 left-8 w-32 h-px bg-gradient-to-r from-blue-500 to-transparent" />
              <div className="absolute top-8 left-8 w-px h-32 bg-gradient-to-b from-blue-500 to-transparent" />
            </div>
            <div className="absolute top-0 right-0 w-64 h-64 z-[4] opacity-20">
              <div className="absolute top-8 right-8 w-32 h-px bg-gradient-to-l from-orange-500 to-transparent" />
              <div className="absolute top-8 right-8 w-px h-32 bg-gradient-to-b from-orange-500 to-transparent" />
            </div>
          </>
        )}
        
        <div className="flex min-h-screen w-full relative z-10">
          {/* Centralized content - AI4ME presentation */}
          <div className="w-full container mx-auto px-4 flex flex-col justify-start md:justify-center pt-20 md:pt-20 pb-32 md:pb-32">
            <div className="max-w-4xl mx-auto text-center space-y-3 md:space-y-6">
              {/* Título unificado para todas as plataformas */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-4 leading-tight text-white text-center px-2" itemProp="headline">
                <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-orange-400 bg-clip-text text-transparent [text-wrap:balance]">
                  Sua parceira de IA comprometida com o sucesso do seu negócio
                </span>
              </h1>
              
              {/* Animated Orb - visível em todas as telas */}
              <div className="flex justify-center mb-4 md:mb-6">
                <SiriOrb 
                  size={isMobile ? "100px" : "160px"} 
                  className="animate-fade-in drop-shadow-2xl"
                  colors={{
                    c1: "hsl(214, 100%, 58%)", // AI4ME blue
                    c2: "hsl(24, 100%, 58%)",  // AI4ME orange  
                    c3: "hsl(200, 100%, 70%)"  // Light blue
                  }}
                  animationDuration={15}
                />
              </div>
              
              {/* Subtítulo */}
              <h2 className="text-base sm:text-lg md:text-2xl font-medium text-blue-100 mb-4 md:mb-6 text-center max-w-3xl mx-auto leading-relaxed px-4 [text-wrap:balance]">
                A AI4ME entende seu momento, se adapta à sua realidade e caminha junto com você — do plano à ação.
              </h2>

              {/* Supporting copy */}
              <div className="flex items-center justify-center">
                <div className="text-sm md:text-lg text-blue-200 mb-2 md:mb-4 max-w-2xl mx-auto leading-relaxed text-center px-4" itemProp="description">
                  <p className="mb-1 [text-wrap:balance]">Orientação estratégica adaptada ao seu nível.</p>
                  <p className="[text-wrap:balance]">Ações práticas que você consegue executar de verdade.</p>
                </div>
              </div>

              {/* CTA Button */}
              <div className="mb-2 md:mb-4 relative z-50">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-base md:text-lg px-6 md:px-8 py-4 md:py-6 min-h-[44px] shadow-2xl hover:shadow-orange-500/50 transition-all duration-300 touch-manipulation"
                  onClick={() => { 
                    navigate('/auth'); 
                    setTimeout(() => window.scrollTo(0, 0), 100);
                  }}
                >
                  Iniciar conversa — é grátis
                </Button>
              </div>

              {/* Microcopy */}
              <p className="text-xs md:text-sm text-blue-200 italic mb-4 md:mb-6">
                Sem compromisso. O plano só é criado se fizer sentido para você.
              </p>

              {/* Scroll Indicator */}
              <div className="animate-bounce">
               <button 
               onClick={() => scrollToSection('referral-cta')}
               className="inline-flex items-center text-blue-200 hover:text-white transition-colors cursor-pointer"
               aria-label="Scroll para próxima seção"
                >
                  <ArrowDown className="w-6 h-6" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </BackgroundGradientAnimation>
    </header>
  );
};

export default Hero;